import React, { Suspense } from 'react';
import { LinearProgress } from '@material-ui/core';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useTypedSelector } from 'redux/reducers';
import { isMsalPopupFlow } from 'services/microsoft/isMsalPopupFlow';
import DashboardRouteShell from 'common/Skeletons/DashboardRouteShell';
import PersistentDashboardRoute from 'layouts/Dashboard/PersistentDashboardRoute';
import PersistentDashboardContext from 'layouts/Dashboard/PersistentDashboardContext';

// Keep route-only features out of the initial bundle. Some of these components
// import the dashboard shell, which in turn reaches large editor/PDF/chart
// dependencies used elsewhere in the application.
const MsalPopupHandler = React.lazy(
  () => import('components/Microsoft/MsalPopupHandler')
);
const FCA = React.lazy(() => import('./components/FCA'));
const ForexTracker = React.lazy(() => import('./components/Forex'));
const NewUserOverLay = React.lazy(() => import('components/LoginDialog'));
const SignBackInDialog = React.lazy(
  () => import('components/Authentication/signedOutLoginDialog')
);
const ExtremeCautionList = React.lazy(
  () => import('./components/DoNotDeal/ExtremeCautionList')
);

const FileStorage = React.lazy(() => import('./views/FileStorage'));
const Extensions = React.lazy(() => import('./views/Extensions'));
const AddressLookup = React.lazy(() => import('./views/Address'));
const Admin = React.lazy(() => import('./views/Admin'));
const Audit = React.lazy(() => import('./views/Audit'));
const CalculatorView = React.lazy(() => import('views/CalculatorView'));
const Chatter = React.lazy(() => import('./views/Chatter'));
const CribSheets = React.lazy(() => import('./views/FileExplorer'));
const VRM = React.lazy(() => import('./views/VRM'));
const Marketing = React.lazy(() => import('./views/Marketing'));
const Customers = React.lazy(() => import('./views/Customers'));
const DashboardRoute = React.lazy(
  () => import('views/Dashboard/DashboardRoute')
);
const Deals = React.lazy(() => import('./views/Deals'));
const DealsCreate = React.lazy(() => import('./views/Deals/Create'));
const DealsEdit = React.lazy(() => import('./views/Deals/Edit'));
const GraphqlCrmView = React.lazy(() => import('views/GraphqlCrmView'));
const InstanceDetail = React.lazy(() => import('./views/InstanceDetail'));
const LandingPage = React.lazy(() => import('./views/LandingPage/Stepper'));
const LegacyDealsViewer = React.lazy(() => import('./views/LegacyDealsViewer'));
const Lenders = React.lazy(() => import('./views/Lenders'));
const Kanban = React.lazy(() => import('./views/Kanban'));
const NotFound = React.lazy(() => import('./views/NotFound'));
const Portal = React.lazy(() => import('./views/Portal'));
const Profile = React.lazy(() => import('./views/Profile'));
const RedFlag = React.lazy(() => import('./views/RedFlag'));
const Settings = React.lazy(() => import('./views/Settings'));
const SignIn = React.lazy(() => import('./views/SignIn'));
const SignUp = React.lazy(() => import('./views/SignUp'));
const SupportPage = React.lazy(() => import('./views/Support'));
const UnderDevelopment = React.lazy(() => import('./views/UnderDevelopment'));
const UserDefinition = React.lazy(() => import('./views/UserDefinition'));
const TestFlight = React.lazy(() => import('./views/TestFlight'));
const PasswordReset = React.lazy(() => import('./views/PasswordReset'));
const MessageHub = React.lazy(() => import('./views/MessageHub'));
const BipsWeekly = React.lazy(() => import('./views/BipsWeekly'));
const InstanceBuilder = React.lazy(() => import('views/InstanceBuilder'));
const BipsGuideAndVideos = React.lazy(() => import('views/BipsGuideAndVideos'));
const DealViewRoute = React.lazy(async () => {
  const module = await import('views/Deal/DealViewRoute');

  return {
    default: module.DealViewRoute as React.ComponentType<any>
  };
});
const CalculatorViewRoute = React.lazy(
  () => import('views/CalculatorView/CalculatorViewRoute')
);

export const PrivateRoute = ({
  component: Component,
  persistentDashboard = true,
  ...props
}) => {
  const { token } = useTypedSelector((s) => s.user.auth);
  const user = useTypedSelector((s) => s.user.user);
  const parentDashboard = React.useContext(PersistentDashboardContext);
  const portalUser = user.SystemAccess === 3;
  const currentPath = props.path;

  const isDashboard = currentPath.includes('/dashboard');

  const route =
    token?.length > 0 ? (
      <>
        <Suspense fallback={null}>
          <SignBackInDialog />
          {process.env.REACT_APP_ENV !== 'production' &&
            !portalUser &&
            isDashboard && <NewUserOverLay />}
        </Suspense>
        {persistentDashboard && !parentDashboard ? (
          <PersistentDashboardRoute>
            <Component {...props} />
          </PersistentDashboardRoute>
        ) : (
          <Suspense fallback={<DashboardRouteShell />}>
            <Component {...props} />
          </Suspense>
        )}
      </>
    ) : (
      <Redirect to="/sign-in" />
    );
  return <Route {...props} render={() => route} />;
};

const Routes = () => {
  const disabled = process.env.REACT_APP_ENV === 'production';
  const user = useTypedSelector((s) => s.user.user);
  const portalUser = user.SystemAccess === 3;

  if (isMsalPopupFlow()) {
    return (
      <Suspense fallback={<LinearProgress />}>
        <MsalPopupHandler />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LinearProgress />}>
      <Switch>
        <Redirect
          exact
          from="/"
          to={
            disabled
              ? '/company-navigator'
              : portalUser
                ? '/portal'
                : '/dashboard'
          }
        />
        <Route
          render={(routeProps) => <DealViewRoute {...routeProps} />}
          exact
          path="/deal/:id"
        />
        <Route
          render={(routeProps) => <CalculatorViewRoute {...routeProps} />}
          exact
          path="/calculator/:id"
        />
        <PrivateRoute component={FileStorage} path="/storage" />
        <PrivateRoute component={InstanceBuilder} path="/noticeboard-Builder" />
        <PrivateRoute component={Extensions} path="/extensions" />
        <PrivateRoute component={AddressLookup} path="/address/lookup" />
        <PrivateRoute component={Admin} path="/admin" />
        <PrivateRoute component={Audit} exact path="/audit" />
        <PrivateRoute component={CalculatorView} exact path="/calculator" />
        <PrivateRoute component={Chatter} exact path="/chatter" />
        <PrivateRoute component={CribSheets} path="/file-explorer" />
        <PrivateRoute component={VRM} path="/vrm" />
        <PrivateRoute component={VRM} path="/vrm/lookup" />
        <PrivateRoute component={Marketing} path="/marketing" />
        <PrivateRoute component={Marketing} path="/marketing/lists" />
        <PrivateRoute component={Marketing} path="/marketing/reports/:id" />
        <PrivateRoute component={Customers} exact path="/customers" />
        <PrivateRoute component={DashboardRoute} exact path="/dashboard" />
        <PrivateRoute component={TestFlight} exact path="/test-flight" />
        <PrivateRoute component={BipsWeekly} exact path="/bips-weekly" />
        <PrivateRoute component={MessageHub} exact path="/message-hub" />
        <PrivateRoute component={Deals} exact path="/deals" />
        <PrivateRoute
          component={DealsCreate}
          exact
          path="/deals/create"
          persistentDashboard={false}
        />
        <PrivateRoute component={DealsEdit} exact path="/deals/edit" />
        <PrivateRoute component={FCA} exact path="/fca" />
        <PrivateRoute component={ForexTracker} exact path="/forex-tracker" />
        <PrivateRoute component={GraphqlCrmView} path="/graphql-crm" />
        <PrivateRoute component={Kanban} exact path="/kanban" />
        <PrivateRoute
          component={LegacyDealsViewer}
          path="/legacy-deals-viewer"
        />
        <PrivateRoute component={Lenders} exact path="/lenders" />
        <PrivateRoute component={Profile} exact path="/profile" />
        <PrivateRoute
          component={Portal}
          exact
          path="/portal"
          persistentDashboard={false}
        />
        <PrivateRoute component={RedFlag} exact path="/company-navigator" />
        <PrivateRoute component={Settings} exact path="/settings" />
        <PrivateRoute component={SupportPage} exact path="/support" />
        <PrivateRoute
          component={ExtremeCautionList}
          exact
          path="/extreme-caution"
        />
        <PrivateRoute component={BipsGuideAndVideos} exact path="/faq" />
        <PrivateRoute
          component={UserDefinition}
          exact
          path="/UserDefinition/:ID"
        />
        <PrivateRoute
          component={InstanceDetail}
          exact
          path="/UserDefinition/:ID/:InstanceId"
        />
        <PrivateRoute
          component={InstanceDetail}
          exact
          path="portal/UserDefinition/:ID/:InstanceId"
        />
        <Route component={SignIn} exact path="/sign-in" />
        <Route
          component={SignIn}
          exact
          path="/sign-in/:pdid/:psdid/:piid/:odid/:oiid/:fdid/:fv/:rid/:msg"
        />
        <Route component={SignUp} exact path="/sign-up" />
        <Route component={LandingPage} exact path="/landingpage" />
        <Route component={PasswordReset} exact path="/passwordreset" />
        <Route component={UnderDevelopment} exact path="/under-development" />
        <Route component={NotFound} exact path="/not-found" />
        <Redirect to="/not-found" />
      </Switch>
    </Suspense>
  );
};

export default Routes;
